// ---------------------------------------------------------------------------
// C3: Sync Configuration UI + C4: Sync Trigger + Log
// ---------------------------------------------------------------------------
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";

interface SyncConfig {
  entities: Array<{ source: string; target: string; direction: "in" | "out" | "bidirectional" }>;
  fieldMapping: Record<string, string>;
  scheduleMinutes: number;
}

interface SyncLogEntry {
  id: string;
  status: string;
  entityType: string | null;
  recordsProcessed: number;
  recordsFailed: number;
  durationMs: number | null;
  startedAt: string;
  completedAt: string | null;
  errorMessage: string | null;
}

const ENTITY_OPTIONS = [
  { value: "contacts", label: "Contacts" },
  { value: "deals", label: "Deals" },
  { value: "companies", label: "Companies" },
  { value: "tasks", label: "Tasks" },
  { value: "notes", label: "Notes" },
];

const SCHEDULE_OPTIONS = [
  { value: 15, label: "Every 15 min" },
  { value: 60, label: "Every hour" },
  { value: 360, label: "Every 6 hours" },
  { value: 1440, label: "Daily" },
];

const DIRECTION_OPTIONS = [
  { value: "in", label: "Import only →" },
  { value: "out", label: "← Export only" },
  { value: "bidirectional", label: "↔ Bidirectional" },
];

export default function SyncConfigPage() {
  const connectionId = window.location.pathname.split("/").at(-2) ?? "";
  const [config, setConfig] = useState<SyncConfig>({ entities: [], fieldMapping: {}, scheduleMinutes: 360 });
  const [logs, setLogs] = useState<SyncLogEntry[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState<"config" | "logs">("config");

  const fetchConfig = useCallback(async () => {
    try {
      const data = await apiFetch<{ syncConfig: SyncConfig }>(`/api/integrations/connections/${connectionId}`);
      if (data.syncConfig) setConfig(data.syncConfig);
    } catch { /* use defaults */ }
  }, [connectionId]);

  const fetchLogs = useCallback(async () => {
    try {
      const data = await apiFetch<{ data: SyncLogEntry[] }>(`/api/integrations/connections/${connectionId}/sync-log`);
      setLogs(data.data ?? []);
    } catch { /* empty */ }
  }, [connectionId]);

  useEffect(() => { fetchConfig(); fetchLogs(); }, [fetchConfig, fetchLogs]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await apiFetch(`/api/integrations/connections/${connectionId}/sync-config`, {
        method: "PUT",
        body: JSON.stringify(config),
      });
    } finally { setIsSaving(false); }
  };

  const handleSyncNow = async () => {
    setIsSyncing(true);
    try {
      await apiFetch(`/api/integrations/connections/${connectionId}/sync`, { method: "POST" });
      setTimeout(() => { fetchLogs(); setIsSyncing(false); }, 2000);
    } catch { setIsSyncing(false); }
  };

  const addEntity = () => {
    setConfig(prev => ({
      ...prev,
      entities: [...prev.entities, { source: "contacts", target: "people", direction: "in" as const }],
    }));
  };

  const removeEntity = (idx: number) => {
    setConfig(prev => ({ ...prev, entities: prev.entities.filter((_, i) => i !== idx) }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-foreground">Sync Configuration</h1>
        <div className="flex gap-2">
          <button onClick={handleSyncNow} disabled={isSyncing} className="rounded-md border border-border px-4 py-2 text-sm hover:bg-accent disabled:opacity-50">
            {isSyncing ? "Syncing..." : "Sync Now"}
          </button>
          <button onClick={handleSave} disabled={isSaving} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50">
            {isSaving ? "Saving..." : "Save Config"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-border">
        {(["config", "logs"] as const).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-2 text-sm font-medium ${activeTab === tab ? "border-b-2 border-primary text-foreground" : "text-muted-foreground"}`}>
            {tab === "config" ? "Entity Mapping" : "Sync History"}
          </button>
        ))}
      </div>

      {activeTab === "config" ? (
        <div className="space-y-4">
          {/* Schedule */}
          <div>
            <label className="text-sm font-medium text-foreground">Sync Schedule</label>
            <select value={config.scheduleMinutes} onChange={e => setConfig(prev => ({ ...prev, scheduleMinutes: Number(e.target.value) }))} className="mt-1 block w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
              {SCHEDULE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {/* Entity mappings */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">Entity Mappings</label>
              <button onClick={addEntity} className="text-sm text-primary">+ Add Mapping</button>
            </div>
            {config.entities.map((entity, idx) => (
              <div key={idx} className="mt-2 flex items-center gap-2 rounded-md border border-border p-3">
                <select value={entity.source} onChange={e => { const updated = [...config.entities]; updated[idx] = { ...entity, source: e.target.value }; setConfig(prev => ({ ...prev, entities: updated })); }} className="rounded border border-border bg-background px-2 py-1 text-sm">
                  {ENTITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <select value={entity.direction} onChange={e => { const updated = [...config.entities]; updated[idx] = { ...entity, direction: e.target.value as "in" | "out" | "bidirectional" }; setConfig(prev => ({ ...prev, entities: updated })); }} className="rounded border border-border bg-background px-2 py-1 text-sm">
                  {DIRECTION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <span className="text-sm text-muted-foreground">→</span>
                <input value={entity.target} onChange={e => { const updated = [...config.entities]; updated[idx] = { ...entity, target: e.target.value }; setConfig(prev => ({ ...prev, entities: updated })); }} className="rounded border border-border bg-background px-2 py-1 text-sm" placeholder="people" />
                <button onClick={() => removeEntity(idx)} className="text-[var(--color-destructive)] hover:text-[var(--color-destructive)]">×</button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {logs.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No sync history yet</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-muted-foreground">
                <th className="pb-2">Status</th><th className="pb-2">Entity</th><th className="pb-2">Processed</th><th className="pb-2">Failed</th><th className="pb-2">Duration</th><th className="pb-2">Started</th>
              </tr></thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id} className="border-b border-border">
                    <td className="py-2"><span className={`rounded-full px-2 py-0.5 text-xs ${log.status === "completed" ? "bg-[var(--color-success)]/10 text-[var(--color-success)]" : log.status === "failed" ? "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]" : "bg-[var(--color-warning)]/10 text-[var(--color-warning)]"}`}>{log.status}</span></td>
                    <td className="py-2">{log.entityType ?? "—"}</td>
                    <td className="py-2">{log.recordsProcessed}</td>
                    <td className="py-2">{log.recordsFailed > 0 ? <span className="text-[var(--color-destructive)]">{log.recordsFailed}</span> : "0"}</td>
                    <td className="py-2">{log.durationMs ? `${(log.durationMs / 1000).toFixed(1)}s` : "—"}</td>
                    <td className="py-2 text-muted-foreground">{new Date(log.startedAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
